import React, { createContext, useContext, useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { Amplify, Auth } from "aws-amplify";
import { USER_AUTH_CONFIG } from "../aws-config";
import { platformState, accessTokenState } from "../global-state";
import { useHistory, useLocation } from "react-router-dom";
import { useAppInterface } from "../app/app-interface-provider";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useRecoilState(accessTokenState);
  const [platform, setPlatform] = useRecoilState(platformState);
  const appInterface = useAppInterface();
  const [userAuthState, setUserAuthState] = useState(false);
  const location = useLocation();
  const history = useHistory();

  const checkUserFromApp = async () => {
    try {
      const token = await appInterface.getAccessToken();
      if (token) {
        setUserAuthState(true);
      } else {
        setUserAuthState(false);
        history.replace("/user-login");
      }
      return token;
    } catch (error) {
      console.error("Error getting access token:", error);
      return null;
    }
  };

  const checkUserFromWeb = async () => {
    let newAccessToken;
    try {
      Amplify.configure(USER_AUTH_CONFIG);
      const session = await Auth.currentSession();
      newAccessToken = session.getAccessToken().getJwtToken();
      setAccessToken(newAccessToken);
      if (newAccessToken) {
        setUserAuthState(true);
      } else {
        setUserAuthState(false);
      }
      return newAccessToken;
    } catch (error) {
      console.error(error);
      handleAuthError(error);
      return null;
    }
  };

  const checkUserFromDesktop = async () => {
    const accessToken = await appInterface.getAccessToken();
    setAccessToken(accessToken);
    if (accessToken) {
      setUserAuthState(true);
    } else {
      setUserAuthState(false);
      history.replace("/user-login");
    }
    return accessToken;
  };

  const checkUserAuthState = async () => {
    if (platform.source === "web") {
      return await checkUserFromWeb();
    } else if (platform.source === "desktop") {
      return await checkUserFromDesktop();
    } else if (platform.source === "app") {
      return await checkUserFromApp();
    } else {
      // TODO
      return null;
    }
  };

  const handleAuthError = (error) => {
    console.log("Auth error:", error.message);
    if (typeof error === "string") {
      if (error.toLowerCase() === "no current user") {
        setUserAuthState(false);
        history.replace("/user-login");
        return;
      }
    }

    if (typeof error === "object" && error !== null) {
      const { name, code, message } = error;
      if (
        name === "NotAuthorizedException" ||
        code === "NotAuthorizedException" ||
        name === "TokenExpiredError" ||
        (message && message.includes("expired")) ||
        (message && message.includes("Invalid token"))
      ) {
        console.log("Token expired or invalid - redirecting to login");
        setUserAuthState(false);
        history.replace("/user-login");
        return;
      }
    }

    console.log("Non-token related error occurred");
    setUserAuthState(false);
  };

  const checkAuthState = async () => {
    if (location.pathname === "/user-login") {
      return;
    }
    try {
      return await checkUserAuthState();
    } catch (error) {
      console.error("Auth check error:", error.message);
      handleAuthError(error);
      return null;
    }
  };

  const refreshUserToken = async () => {
    if (platform.source === "web") {
      try {
        Amplify.configure(USER_AUTH_CONFIG);
        const session = await Auth.currentSession();
        setAccessToken(session.getAccessToken().getJwtToken());
      } catch (error) {
        setUserAuthState(false);
        handleAuthError(error);
      }
    }
  };

  useEffect(() => {
    Amplify.configure(USER_AUTH_CONFIG);
    checkAuthState();

    const intervalId = setInterval(() => {
      refreshUserToken();
    }, 3 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  const value = {
    userAuthState,
    checkAuthState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
